import { HttpParams } from '@angular/common/http';
import { AfterContentInit, Component, ContentChildren, ElementRef, forwardRef, Inject, Input, OnInit, QueryList, ViewChild, ViewContainerRef } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import * as _ from "lodash";
import { firstValueFrom, take } from 'rxjs';
import { Permissions } from '../../common/constants';
import { EXPORT_REPORT } from '../../common/endpoints';
import { PropertyInfo } from '../../common/properties';
import { ThingService as DashboardAreaThingService } from '../../dashboard-area/thing/thing.service';
import { ActionExecutionState, ActionState, ActionStatus, ActivationType, DefaultPlugin, Plugin, Thing, WorkOrder, WorkOrderStatus } from '../../model';
import { ExportReportJobKey } from '../../model/export-report-job-key';
import { AbstractExportContextService } from '../../service/abstract-export-context.service';
import { ActionContextService } from '../../service/action-context.service';
import { AppService } from '../../service/app.service';
import { AuthenticationService } from '../../service/authentication.service';
import { ContextService } from '../../service/context.service';
import { CustomPropertyService, CustomPropertyType } from '../../service/custom-property.service';
import { DownloadService } from '../../service/download.service';
import { FieldService } from '../../service/field.service';
import { HttpService } from '../../service/http.service';
import { NavigationService } from '../../service/navigation.service';
import { StatisticService } from '../../service/statistic.service';
import { ThingContextService } from '../../service/thing-context.service';
import { ThingService } from '../../service/thing.service';
import { UserActionStatusService } from '../../service/user-action-status.service';
import { AbstractContextService } from '../../shared/class/abstract-context-service.class';
import { ListSnackbarButtonType } from '../../shared/component/list-snackbar/list-snackbar.component';
import { ConfirmDialog } from '../../shared/confirm-dialog/confirm-dialog.component';
import { ButtonActionValue, CustomTableColumn, CustomTableService } from '../../shared/custom-table';
import { DownloadStatus, DownloadType } from '../../shared/download-dialog/download-dialog.component';
import { ClickOnRowBehaviour, ListWidgetV2Component } from '../../shared/list-widget-v2/list-widget-v2.components';
import { DatetimeFormatterPipe, LocalizationPipe } from '../../shared/pipe';
import { ActionExecutionStatePipe } from '../../shared/pipe/action-execution-state.pipe';
import { SearchFieldComponent, SearchFieldInput, SearchFieldInputType } from '../../shared/search-field/search-field.component';
import { COMPONENT_DEFINITION_REF } from "../../shared/utility/component-definition-token";
import { ErrorUtility } from '../../utility/error-utility';
import { ActionInfoDialogV2Component } from '../action-info-dialog/action-info-dialog-v2.component';
import { ActionListWidgetReschedulingDialogComponent } from '../action-list/action-list-widget-rescheduling-dialog.component';
import { SnackbarService } from '../shared/snackbar.service';
import { WidgetPrintResizeCoordinatorService } from '../shared/widget-print-resize-coordinator.service';
import { ThingListWidgetV2Service } from '../thing-list/thing-list-widget-v2.service';
import { ActionListWidgetV2Service } from './action-list-widget-v2.service';
import { AddActionDialogComponent } from './add-action-dialog.component';
import { AddToWorkOrderDialogComponent } from './add-to-work-order-dialog.component';
import { DiscardActionDialogComponent } from './discard-action-dialog.component';
import { MarkAsDoneWithIssueActionDialogComponent } from './mark-as-done-with-issue-action-dialog.component';
import { ResyncActionsDialogComponent } from './resync-actions-dialog.component';
import { WorkOrderEditDialogComponent } from './work-order-edit-dialog.component';
import { WorkOrderListDialogComponent } from './work-order-list-dialog.component';
import { WorkOrderSparePartListDialogComponent } from './work-order-spare-part-list-dialog.component';

@Component({
    selector: 'action-list-widget-v2',
    template: require('./action-list-widget-v2.component.html'),
    styles: [require('../../shared/list-widget-v2/list-widget-v2.css'), require('../action-list-v2/action-list-widget-v2.component.css')],
    providers: [ActionListWidgetV2Service, ThingListWidgetV2Service, DashboardAreaThingService]
})
export class ActionListWidgetV2Component extends ListWidgetV2Component<ActionStatus> implements OnInit, AfterContentInit {

    @Input() searchFields: string[] = ['state', 'actionDefinition.name', 'actionDefinition.label', 'actionDefinition.description', 'actionDefinition.group'];

    @Input() addActionEnabled: boolean = true;

    @Input() status: ActionListWidgetStatus = ActionListWidgetStatus.ALL;

    @ContentChildren(COMPONENT_DEFINITION_REF) private columnComponents: QueryList<any>;

    @ViewChild(ActionListWidgetReschedulingDialogComponent) reschedulingDialog: ActionListWidgetReschedulingDialogComponent;

    hidden: boolean;
    isActive: boolean = true;
    showAdd: boolean;
    thingId: string;
    showWorkOrders: boolean;
    writeWorkOrders: boolean;
    workOrders: WorkOrder[];
    workOrderContext: WorkOrder;
    actionSyncLoading: boolean;
    workingHours: number;
    simulatedWorkingHours: number;
    updateWorkingHoursDisabled: boolean;

    private actionStatuses: ActionStatus[];
    private defaultProperties: { [name: string]: PropertyInfo } = {
        icon: { label: '', path: 'actionDefinition.iconUrl', defaultFilter: null, defaultSorting: null },
        state: { label: 'statusProperty', path: 'state', defaultFilter: null, defaultSorting: null },
        "actionDefinition.label": { label: 'actionProperty', path: 'actionDefinition.label', defaultFilter: null, defaultSorting: null },
        startTimestamp: { label: 'startDateProperty', path: 'startTimestamp', defaultFilter: DatetimeFormatterPipe, defaultSorting: null }
    };
    protected defaultSearchFieldInputs: SearchFieldInput[] = [
        { type: SearchFieldInputType.PROPERTY, property: 'actionDefinitionTypes', columnComponents: null },
        { type: SearchFieldInputType.PROPERTY, property: 'topics', columnComponents: null },
        { type: SearchFieldInputType.KEY, property: null, columnComponents: null },
        { type: SearchFieldInputType.ADVANCED, property: null, columnComponents: null },
        { type: SearchFieldInputType.CLEAR, property: null, columnComponents: null }
    ];
    private actionsSynchronizerPlugin: Plugin;
    private actionSyncTimestampMap: { [thingId: string]: number };
    private actionAutomaticSyncInterval: number = 60 * 5 * 1000; // 5 minutes 
    private updateWorkingHoursButtonVisible: boolean;
    private initialSort: string[];
    private isWorkOrderHistoryDialogOpen: boolean;

    static ACTION_SYNC_TIMESTAMP_MAP_KEY = 'actionSyncTimestampMap';
    static DEFAULT_WORK_ORDER_REPORT_FILE_NAME = 'WORK_ORDER_REPORT_${thing.serialNumber}_${workOrder.startTimestamp.toDateFormat}.pdf';
    static WORK_ORDER_REPORT_FILE_NAME_DATE_FORMAT = 'yyyy_MM_DD_HHmm';

    constructor(
        @Inject(forwardRef(() => ActionListWidgetV2Service)) private actionListWidgetV2Service: ActionListWidgetV2Service,
        @Inject(forwardRef(() => AuthenticationService)) authenticationService: AuthenticationService,
        @Inject(forwardRef(() => LocalizationPipe)) private localizationPipe: LocalizationPipe,
        @Inject(forwardRef(() => AbstractContextService)) private contextService: AbstractContextService,
        @Inject(forwardRef(() => ActionContextService)) private actionContextService: ActionContextService,
        @Inject(forwardRef(() => NavigationService)) private navigationService: NavigationService,
        @Inject(forwardRef(() => AppService)) appService: AppService,
        @Inject(forwardRef(() => FieldService)) protected fieldService: FieldService,
        @Inject(forwardRef(() => ThingListWidgetV2Service)) private thingListWidgetService: ThingListWidgetV2Service,
        @Inject(forwardRef(() => AbstractExportContextService)) exportService: AbstractExportContextService,
        @Inject(forwardRef(() => ThingContextService)) private thingContextService: ThingContextService,
        @Inject(forwardRef(() => MatDialog)) dialog: MatDialog,
        @Inject(forwardRef(() => ViewContainerRef)) private vcRef: ViewContainerRef,
        @Inject(forwardRef(() => UserActionStatusService)) private userActionStatusService: UserActionStatusService,
        @Inject(forwardRef(() => SnackbarService)) snackbarService: SnackbarService,
        @Inject(forwardRef(() => StatisticService)) private statisticService: StatisticService,
        @Inject(forwardRef(() => CustomPropertyService)) private customPropertyService: CustomPropertyService,
        @Inject(forwardRef(() => ThingService)) private thingService: ThingService,
        @Inject(forwardRef(() => DashboardAreaThingService)) private dashboardAreaThingService: DashboardAreaThingService,
        @Inject(forwardRef(() => DownloadService)) private downloadService: DownloadService,
        @Inject(forwardRef(() => HttpService)) private httpService: HttpService,
        @Inject(forwardRef(() => ContextService)) private timezoneContextService: ContextService,
        @Inject(forwardRef(() => DatetimeFormatterPipe)) private datetimeFormatterPipe: DatetimeFormatterPipe,
        @Inject(forwardRef(() => ElementRef)) elementRef: ElementRef,
        @Inject(forwardRef(() => WidgetPrintResizeCoordinatorService)) coordinator: WidgetPrintResizeCoordinatorService,
    ) {
        super(appService, authenticationService, exportService, null, dialog, snackbarService, null, elementRef, coordinator);
    }

    ngOnInit() {
        this.searchFieldId = 'action-list-query';
        this.checkIfHidden();
        this.readPermission = this.authenticationService.isCustomerUser() || this.authenticationService.isLocationUser()
            || ((this.authenticationService.isOrganizationUser() || this.authenticationService.isPartnerUser()) && !!this.contextService.getCurrentCustomer());
        this.showWorkOrders = this.authenticationService.hasPermission(Permissions.READ_WORK_ORDER);
        this.writeWorkOrders = this.authenticationService.hasPermission(Permissions.WRITE_WORK_ORDER);
        this.isActive = this.status != ActionListWidgetStatus.HISTORICAL;
        this.actionListWidgetV2Service.setActive(this.isActive);
        this.waitForAdvancedSearchInit = true;
        this.subscribeToPeriodField();
        this.preserveSelectedBetweenPages = true;
        this.initWorkOrders();
        if (this.isOnline()) {
            this.initActionSync();
            if (this.exportEnabled) {
                this.subscribeToExportServices();
            }
        }
    }

    private initWorkOrders(): Promise<any> {
        this.workOrders = null;
        if (this.showWorkOrders && this.thingId) {
            return this.actionListWidgetV2Service.getRecursivelyAllWorkOrders(this.thingId, true).then(workOrders => {
                this.workOrders = workOrders;
            });
        }
        return Promise.resolve();
    }

    private initActionSync(): void {
        this.actionsSynchronizerPlugin = this.authenticationService.getEnabledPlugins().find(p => p.name == DefaultPlugin.ACTIONS_SYNCHRONIZER);
        if (this.thingId && !!this.actionsSynchronizerPlugin) {
            const workingHoursPropertyDefinition = this.customPropertyService.getCustomPropertyDefinitionByTypeAndName(CustomPropertyType.Thing, 'working_hours');
            const simulatedWorkingHoursPropertyDefinition = this.customPropertyService.getCustomPropertyDefinitionByTypeAndName(CustomPropertyType.Thing, 'simulated_working_hours');
            this.updateWorkingHoursButtonVisible = !!workingHoursPropertyDefinition;
            this.updateWorkingHoursDisabled = !this.authenticationService.hasPermission(Permissions.WRITE_THING) || !simulatedWorkingHoursPropertyDefinition;
            this.actionSyncTimestampMap = this.getActionSyncTimestampMapFromLocalStorage();
            this.getWorkingHoursPropertyValues();
            const now = new Date().getTime();
            if (!this.actionSyncTimestampMap[this.thingId] || (now - this.actionSyncTimestampMap[this.thingId]) > this.actionAutomaticSyncInterval) {
                this.resync();
            }
        }
    }

    private checkIfHidden(): void {
        if (!this.thingContextService.getCurrentThing()) {
            if (this.authenticationService.isOrganizationUser() || this.authenticationService.isPartnerUser()) {
                this.hidden = !this.authenticationService.hasFeature('multipleThingAggregationForOrgPartner');
            } else {
                this.hidden = !this.authenticationService.hasFeature('multipleThingAggregationForCustomer');
            }
        } else {
            this.thingId = this.thingContextService.getCurrentThing().id;
            this.showAdd = true;
        }
    }

    showCreateWorkOrder(): boolean {
        return this.isActive && this.loaded && this.thingId && this.showWorkOrders && this.writeWorkOrders && !this.workOrderContext;
    }

    showUpdateWorkingHoursButton(): boolean {
        return this.isActive && this.loaded && this.thingId && !!this.actionsSynchronizerPlugin && this.updateWorkingHoursButtonVisible;
    }

    ngAfterContentInit(): void {
        if (this.readPermission) {
            this.getDisplayedColumns([]);
            if (!this.sort) {
                this.sort = this.actionListWidgetV2Service.setDefaultSort(this.displayedColumns);
            }
            this.initialSort = this.sort;
            setTimeout(() => {      //waits for search field init
                this.getSearchFieldInputs();
                this.initCompleted = true;
                if (!this.isOnline()) {
                    this.executeAdvancedSearch(null);
                }
            });
        }
    }

    private getDisplayedColumns(actionStatuses: ActionStatus[]): void {
        const components = this.columnComponents.toArray().filter(col => !(col instanceof SearchFieldComponent));
        this.displayedColumns = this.actionListWidgetV2Service.getVisibleColumns(components, this.defaultProperties, 'ActionStatus');
        let iconValueMapReschedule = {
            "_default": {
                isFontAwesome: false,
                customIconClass: 'material-symbols-outlined action-reschedule-icon text-default',
                customIconHtml: 'next_plan'
            }
        }
        if (actionStatuses?.some(a => a['showReschedule'])) {
            this.displayedColumns.push(
                CustomTableService.newIconColumn('reschedule', '', 'showReschedule', iconValueMapReschedule).withVisiblePath('showReschedule').withStyle({ '_any': { 'padding-top': '4px', 'width': '1%' } }).withColumnClass('no-print')
            );
        }
        if (this.clickOnRowBehaviour == ClickOnRowBehaviour.OPEN_CONTEXT_OBJECT) {
            let iconValueMapInfo = {
                "_default": {
                    isFontAwesome: false,
                    customIconClass: 'material-symbols-outlined action-info-icon text-default',
                    customIconHtml: 'info'
                }
            }
            this.displayedColumns.push(
                CustomTableService.newIconColumn('info', '', 'showInfo', iconValueMapInfo).withStyle({ '_any': { 'padding-top': '4px', 'width': '1%' } }).withColumnClass('no-print')
            );
        }
        if (this.isActive && !this.showWorkOrders) {
            this.displayedColumns = this.displayedColumns.filter(c => !c.name.startsWith('workOrder'));
        } else {
            if (this.workOrderContext) {
                this.displayedColumns = this.displayedColumns.filter(c => !c.name.startsWith('workOrder'));
                if (this.isActive) {
                    this.displayedColumns.push(CustomTableService.newActionExecutionColumn(this.workOrderContext.status != WorkOrderStatus.EXECUTION).withSortField('executionState'));
                } else {
                    this.displayedColumns = this.displayedColumns.filter(col => col.sortField != 'state');
                    this.displayedColumns.push(
                        CustomTableService.newPipedColumn('executionState', 'executionProperty', 'state', ActionExecutionStatePipe).withArgument('workOrder.status', true).withSortField('state')
                    );
                }
            } else {
                this.displayedColumns.push(
                    CustomTableService.newPipedColumn('executionState', 'executionProperty', this.isActive ? 'executionState' : 'state', ActionExecutionStatePipe).withArgument('workOrder.status', true).withSortField(this.isActive ? 'executionState' : null)
                );
            }
        }
    }

    private getActionList(): void {
        this.actionListWidgetV2Service.getPagedList(this.pageIndex, this.pageSize, this.sort, this.searchFields, this.advancedSearchBody, this.workOrderContext?.id).then(pagedList => {
            this.actionStatuses = pagedList.content;
            this.setIconsVisiblities(pagedList.content);
            this.updateRowClasses(pagedList.content);
            this.getDisplayedColumns(pagedList.content);
            let things: Thing[] = pagedList.content.filter(as => as.thing).map(as => { return as.thing });
            this.thingListWidgetService.addTags(things, this.contextService.getTagObjects());
            this.updateElementList(pagedList, !this.isActive);
            this.error = null;
        }).catch(err => this.error = ErrorUtility.getMessage(err));
    }

    refreshList(data?: { pageIndex: number, pageSize: number, advancedSearchBody: any, sort: string[] }): void {
        this.loaded = false;
        if (data) {
            this.pageIndex = data.pageIndex;
            this.pageSize = data.pageSize;
            this.advancedSearchBody = data.advancedSearchBody;
            this.sort = data.sort;
        }
        this.getActionList();
    }

    private setIconsVisiblities(elements: ActionStatus[]): void {
        elements.forEach(el => this.setIconsVisibility(el));
    }

    private setIconsVisibility(el: ActionStatus): void {
        if (this.isActive && !el.workOrderId) {
            el['showReschedule'] = el.actionDefinition?.activationType == ActivationType.PERIOD && el.actionDefinition?.reschedulable ? this.localizationPipe.transform('Reschedule') : null;
        }
        el['showInfo'] = this.localizationPipe.transform('Info');
    }

    execButtonAction(actionValue: ButtonActionValue): void {
        const index = actionValue.index;
        const actionStatus = this.actionStatuses[index];
        if (actionValue.action == 'updateExecutionState') {
            const executionState = actionValue.value;
            if (executionState == ActionExecutionState.DISCARDED) {
                this.openDiscardDialog(actionStatus, index);
            } else if (executionState == ActionExecutionState.DONE_WITH_ISSUE) {
                this.openMarkAsDoneWithIssueDialog(actionStatus, index);
            } else if (executionState == ActionExecutionState.DONE) {
                if (actionStatus.executionState) {
                    this.openConfirmDialog(actionStatus, index);
                } else {
                    this.updateActionState({ executionState: actionValue.value }, actionStatus, index);
                }
            } else {
                this.updateActionState({ executionState: actionValue.value }, actionStatus, index);
            }
        } else if (actionValue.action == 'reschedule') {
            this.reschedulingDialog.open(actionStatus);
        } else if (actionValue.action == 'info') {
            this.openActionInfoDialog(actionStatus);
        } else if (actionValue.action == 'openWorkOrder') {
            let workOrder = actionValue.value;
            if (this.isActive) { // retrieve the enriched
                workOrder = this.workOrders.find(w => w.id == actionStatus.workOrderId);
            } else { // sets the id to filter actions
                workOrder.id = actionStatus.workOrderId;
            }
            this.setWorkOrderContext(workOrder);
        }
    }

    private openConfirmDialog(actionStatus: ActionStatus, index: number): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '25%';
        dialogConfig.autoFocus = false;
        dialogConfig.data = {
            title: "markAsDoneActionMessage",
            message: "markAsDoneActionInfoMessage"
        }
        this.dialog.open(ConfirmDialog, dialogConfig).afterClosed().pipe(take(1)).subscribe(result => {
            if (result) {
                this.updateActionState({ executionState: ActionExecutionState.DONE }, actionStatus, index);
            } else {
                this.refreshElementList();
                this.actionContextService.updateRefreshEventSubject(true);
            }
        });
    }

    private updateActionState(body: any, actionStatus: ActionStatus, index: number): void {
        this.userActionStatusService.patchActionStatus(actionStatus, body, !this.isActive).then(() => {
            this.refreshAction(actionStatus, index);
        }).catch(err => this.error = ErrorUtility.getMessage(err));
    }

    private refreshAction(actionStatus: ActionStatus, index: number): Promise<void> {
        return this.userActionStatusService.getActionStatusById(actionStatus.id, !this.isActive)
            .then(a => {
                if ((a.state == ActionState.DONE || a.state == ActionState.DISCARDED) && this.isActive) {
                    this.actionStatuses.splice(index, 1);
                } else {
                    this.setIconsVisibility(a);
                    this.updateRowClass(a);
                    this.actionStatuses[index] = a;
                }
            }).catch(() => this.actionStatuses.splice(index, 1))
            .then(() => {
                this.elements = this.actionStatuses;
                this.dataSource = new MatTableDataSource<ActionStatus>(this.elements);
            });
    }

    goToDetail(status: ActionStatus): void {
        if (status && status.thingId && this.clickOnRowBehaviour == ClickOnRowBehaviour.OPEN_CONTEXT_OBJECT) {
            this.navigationService.goToThingDetailPage(status.thingId);
        } else if (status) {
            this.openActionInfoDialog(status);
        }
    }

    private updateRowClasses(elements: ActionStatus[]): void {
        elements.forEach(as => this.updateRowClass(as));
    }

    private updateRowClass(as: ActionStatus): void {
        as['rowClass'] = as.actionDefinition?.priority;
    }

    protected subscribeToExportServices(): void {
        this.exportId = "action-list-" + this.getNextId();
        this.exportService.subscribeToExport(this.exportId, this.title || "Actions").subscribe(() => this.export());
        this.subscribeToExportVisibility();
    }

    export(): void {
        const params = this.actionListWidgetV2Service.getSearchParams(this.searchFields, this.advancedSearchBody, this.workOrderContext?.id, true);
        this.actionListWidgetV2Service.downloadCSV(params, this.exportService.resolveExportFileNamePlaceholders(this.exportFileName));
    }

    rescheduleAction(rescheduleBody: any): void {
        const actionStatusId = rescheduleBody.actionStatusId;
        delete rescheduleBody.actionStatusId;
        const index = this.actionStatuses.findIndex(el => el.id == actionStatusId)
        this.updateActionState(rescheduleBody, this.actionStatuses[index], index);
    }

    private openActionInfoDialog(actionStatus: ActionStatus): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '25%';
        dialogConfig.width = '600px';
        dialogConfig.autoFocus = false;
        dialogConfig.data = { actionStatus: actionStatus, isHistorical: !this.isActive, workOrderContext: this.workOrderContext };
        dialogConfig.viewContainerRef = this.vcRef;
        dialogConfig.disableClose = true;
        this.dialog.open(ActionInfoDialogV2Component, dialogConfig).afterClosed().pipe(take(1)).subscribe(refresh => {
            if (refresh) {
                this.refreshAction(actionStatus, this.actionStatuses.findIndex(a => a.id == actionStatus.id));
                if (this.workOrderContext) {
                    this.actionListWidgetV2Service.getWorkOrder(this.workOrderContext.id).then(wo => this.setWorkOrderContext(wo));
                }
            }
        });
    }

    changeStatus(newStatus: boolean): void {
        this.isActive = newStatus;
        this.actionListWidgetV2Service.setActive(this.isActive);
        this.sort = this.isActive ? this.initialSort : [];
        this.elements = [];
        this.selectedElements = [];
        this.allElementsSelected = false;
        this.loaded = false;
        this.pageIndex = 0;
        this.initCompleted = false;
        setTimeout(() => {
            this.initCompleted = true;
            if (!this.isOnline()) {
                this.executeAdvancedSearch(null);
            }
        }, 10);
    }

    addAction(): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '75%';
        dialogConfig.autoFocus = false;
        dialogConfig.data = {
            thing: this.thingContextService.getCurrentThing(),
            actionListWidgetV2Service: this.actionListWidgetV2Service,
            isActive: this.isActive,
            displayedColumns: this.getAddActionDialogDisplayColumns(),
            workOrder: this.workOrderContext
        }
        this.dialog.open(AddActionDialogComponent, dialogConfig).afterClosed().pipe(take(1)).subscribe(action => {
            if (action) {
                this.refreshElementList();
                if (this.workOrderContext) {
                    this.actionListWidgetV2Service.getWorkOrder(this.workOrderContext.id).then(wo => this.setWorkOrderContext(wo));
                }
                this.actionContextService.updateRefreshEventSubject(true);
                if (!this.isActive && action.id) {
                    this.userActionStatusService.getActionStatusById(action.id, true).then(action => {
                        this.openActionInfoDialog(action);
                    });
                }
            }
        });
    }

    private getAddActionDialogDisplayColumns(): CustomTableColumn[] {
        const offset = "actionDefinition.".length;
        return _.cloneDeep(this.displayedColumns).filter(c => c.name.startsWith("actionDefinition.")).map(c => {
            c.path = c.path.substring(offset);
            c.sortField = c.path;
            return c;
        });
    }

    private openDiscardDialog(actionStatus?: ActionStatus, index?: number): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '25%';
        dialogConfig.maxWidth = '428px';
        dialogConfig.panelClass = "discard-action-confirm-dialog";
        dialogConfig.autoFocus = false;
        dialogConfig.data = { actionStatus: actionStatus };
        this.dialog.open(DiscardActionDialogComponent, dialogConfig).afterClosed().pipe(take(1)).subscribe(body => {
            if (body) {
                if (actionStatus) {
                    if (actionStatus.workOrderId) {
                        body.executionState = ActionExecutionState.DISCARDED;
                    } else {
                        body.state = ActionState.DISCARDED;
                    }
                    this.updateActionState(body, actionStatus, index);
                } else {
                    body.elementIds = this.allElementsSelected ? [] : this.selectedElements.map(e => e.id);
                    this.actionListWidgetV2Service.bulkMarkAsDiscarded(this.searchFields, this.advancedSearchBody, this.workOrderContext?.id, body, this.allElementsSelected).then(resp => {
                        this.clearSelectedElements();
                        this.actionListWidgetV2Service.getWorkOrder(this.workOrderContext.id).then(wo => this.setWorkOrderContext(wo));
                        this.showSnackbar(`${resp.count} ${this.localizationPipe.transform('actions marked as discarded')}`);
                    }).catch(err => this.error = ErrorUtility.getMessage(err));
                }
            } else if (actionStatus) {
                this.refreshElementList();
                this.actionContextService.updateRefreshEventSubject(true);
            }
        });
    }

    private openMarkAsDoneWithIssueDialog(actionStatus: ActionStatus, index: number): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '25%';
        dialogConfig.maxWidth = '428px';
        dialogConfig.panelClass = "mark-as-done-with-issue-action-confirm-dialog";
        dialogConfig.autoFocus = false;
        dialogConfig.data = { actionStatus: actionStatus };
        this.dialog.open(MarkAsDoneWithIssueActionDialogComponent, dialogConfig).afterClosed().pipe(take(1)).subscribe(body => {
            if (body) {
                if (actionStatus.workOrderId) {
                    body.executionState = ActionExecutionState.DONE_WITH_ISSUE;
                } else {
                    body.state = ActionState.DONE;
                }
                this.updateActionState(body, actionStatus, index);
            } else {
                this.refreshElementList();
                this.actionContextService.updateRefreshEventSubject(true);
            }
        });
    }

    createWorkOrder(withSelection?: boolean): Promise<any> {
        this.loaded = false;
        let actionStatusIds = [];
        if (withSelection && !this.allElementsSelected) {
            actionStatusIds = this.selectedElements.map(a => a.id);
        }
        return this.actionListWidgetV2Service.createWorkOrder(actionStatusIds, this.thingId)
            .then(workOrder => {
                let bulkAddPromise: Promise<any> = Promise.resolve();
                if (!actionStatusIds.length) {
                    bulkAddPromise = this.actionListWidgetV2Service.bulkAddToWorkOrder(this.searchFields, this.advancedSearchBody, workOrder.id, [], true);
                }
                return bulkAddPromise.then(() => {
                    this.initWorkOrders();
                    if (!withSelection) { // no context change
                        this.actionListWidgetV2Service.getWorkOrder(workOrder.id).then(wo => this.setWorkOrderContext(wo));
                    }
                }).catch(err => this.error = ErrorUtility.getMessage(err));;
            }).catch(err => this.error = ErrorUtility.getMessage(err));
    }

    setWorkOrderContext(workOrder: WorkOrder): void {
        this.workOrderContext = workOrder;
        this.actionListWidgetV2Service.setWorkOrderId(workOrder?.id);
        if (workOrder) {
            this.isActive = !this.isHistorical(workOrder);
            this.actionListWidgetV2Service.setActive(this.isActive);
        } else {
            this.initWorkOrders();
            if (!this.isActive) {
                this.changeStatus(true);
                if (this.isWorkOrderHistoryDialogOpen) {
                    this.openWorkOrderHistory();
                }
            }
        }
        this.clearSelectedElements();
        this.refreshList();
    }

    isHistorical(workOrder: WorkOrder): boolean {
        return workOrder.status == WorkOrderStatus.COMPLETED || workOrder.status == WorkOrderStatus.DISCARDED;
    }

    protected updateSnackbarButtons(): void {
        if (this.selectedElements.length) {
            const allElementsCount = this.allElementsSelected ? this.length : this.selectedElements.length;
            let dividerPosition = 0;
            this.snackbarButtons = [
                { type: ListSnackbarButtonType.SELECTED_ELEMENTS, label: allElementsCount + '', event: 'unselect' }
            ];
            if (!this.allElementsSelected) {
                this.snackbarButtons.push({ type: ListSnackbarButtonType.SIMPLE, label: 'selectAllButton', event: 'selectAll', icon: 'select_all' });
                dividerPosition++;
            }
            let showDivider = false;
            if (this.writeWorkOrders) {
                if (this.workOrderContext) {
                    if (this.workOrderContext.status == WorkOrderStatus.EXECUTION && (this.allElementsSelected || this.selectedElements.some(a => this.canManageAction(a)))) {
                        this.snackbarButtons.push({
                            type: ListSnackbarButtonType.MENU, label: 'executeButton', icon: 'task_alt', menuButtons: [
                                { label: 'markAsDiscardedProperty', event: 'markAsDiscarded' }
                            ]
                        });
                        showDivider = true;
                    }
                    if (this.workOrderContext.status == WorkOrderStatus.DRAFT && (this.allElementsSelected || this.selectedElements.some(a => this.canManageAction(a)))) {
                        this.snackbarButtons.push({ type: ListSnackbarButtonType.SIMPLE, label: 'removeFromWorkOrderProperty', event: 'removeFromWorkOrder', icon: 'playlist_remove' });
                        showDivider = true;
                    }
                    if (this.workOrderContext.status == WorkOrderStatus.EXECUTION && (this.allElementsSelected || this.selectedElements.some(a => a.workOrderNative && this.canManageAction(a)))) {
                        this.snackbarButtons.push({ type: ListSnackbarButtonType.SIMPLE, label: 'deleteButton', event: 'deleteWorkOrderNative', icon: 'delete' });
                        showDivider = true;
                    }
                } else { // !workOrderContext
                    if (this.allElementsSelected) {
                        this.existsAnyActionWithoutWorkOrder().then(exists => {
                            if (exists) {
                                this.snackbarButtons.push({ type: ListSnackbarButtonType.SIMPLE, label: 'addToWorkOrderProperty', event: 'addToWorkOrder', icon: 'playlist_add' });
                                this.dividerIndexes = [dividerPosition];
                            }
                        });

                    } else if (this.selectedElements.some(a => !a.workOrderId && this.canManageAction(a))) {
                        this.snackbarButtons.push({ type: ListSnackbarButtonType.SIMPLE, label: 'addToWorkOrderProperty', event: 'addToWorkOrder', icon: 'playlist_add' });
                        showDivider = true;
                    }
                }
            }
            this.dividerIndexes = showDivider ? [dividerPosition] : null;
        } else {
            this.snackbarButtons = null;
            this.dividerIndexes = null;
        }
    }

    private canManageAction(action: ActionStatus): boolean {
        const actionDef = action.actionDefinition;
        if (!actionDef) {
            return true;
        }
        if (!actionDef.userTypeFiltered) {
            return true;
        }
        const userTypeId = this.authenticationService.getUser().userTypeId;
        return actionDef.writeUserTypeIds?.includes(userTypeId);
    }

    private existsAnyActionWithoutWorkOrder(): Promise<boolean> {
        let params = new HttpParams();
        params = params.set('resource', 'action');
        params = params.set('aggregation', 'COUNT');
        params = params.set('thingId', this.thingId);
        params = params.set('workOrderId', `exists;false`);
        if (this.advancedSearchBody) {
            if (this.advancedSearchBody.key) {
                params = params.set('searchText', "*" + this.advancedSearchBody.key + "*");
                this.searchFields.forEach(field => params = params.append('searchField', field));
            }
            if (this.advancedSearchBody.topics?.length) {
                this.advancedSearchBody.topics.forEach(topic => params = params.append('topic', topic));
            }
            if (this.advancedSearchBody.actionDefinitions?.length) {
                this.advancedSearchBody.actionDefinitions.forEach(actionDefinitionId => params = params.append('actionDefinitionId', actionDefinitionId));
            }
            if (this.advancedSearchBody.priorities?.length) {
                this.advancedSearchBody.priorities.forEach(priority => params = params.append('priority', priority));
            }
            if (this.advancedSearchBody.statuses?.length) {
                this.advancedSearchBody.statuses.forEach(state => params = params.append('state', state));
            }
            if (this.advancedSearchBody.selectedThingIds?.length) {
                this.advancedSearchBody.selectedThingIds.forEach(id => params = params.append('selectedThingId', id));
            }
            if (this.advancedSearchBody.startTimestamp) {
                params = params.set('minStartTimestamp', this.advancedSearchBody.startTimestamp);
            }
            if (this.advancedSearchBody.endTimestamp) {
                params = params.set('maxStartTimestamp', this.advancedSearchBody.endTimestamp);
            }
            if (this.advancedSearchBody.actionDefinitionTypes?.length) {
                this.advancedSearchBody.actionDefinitionTypes.forEach(type => params = params.append('actionDefinitionType', type));
            }
        }
        return this.statisticService.getStatisticPromise(null, params).then(results => {
            const count = results[0];
            return !!count?.value;
        });
    }

    doAction(event: string): void {
        switch (event) {
            case 'unselect':
                this.clearSelectedElements();
                break;
            case 'selectAll':
                this.setAllElementsSelected();
                break;
            case 'markAsDiscarded':
                this.bulkMarkAsDiscarded();
                break;
            case 'addToWorkOrder':
                this.openWorkOrderSelectionDialog();
                break;
            case 'removeFromWorkOrder':
                this.removeFromWorkOrder();
                break;
            case 'deleteWorkOrderNative':
                this.bulkDeleteWorkOrderNative();
                break;
        }
    }


    private openWorkOrderSelectionDialog(): void {
        const selectableWorkOrders = this.workOrders?.filter(w => WorkOrderStatus.DRAFT == w.status || WorkOrderStatus.EXECUTION == w.status);
        if (selectableWorkOrders?.length) {
            const dialogConfig = new MatDialogConfig();
            dialogConfig.autoFocus = false;
            dialogConfig.maxWidth = '320px';
            dialogConfig.data = {
                workOrders: selectableWorkOrders
            }
            this.dialog.open(AddToWorkOrderDialogComponent, dialogConfig).afterClosed().pipe(take(1)).subscribe(result => {
                if (result) {
                    if (result.new) {
                        this.createWorkOrder(true).then(() => {
                            this.clearSelectedElements();
                            this.refreshList();
                        });
                    } else {
                        const elementIds = this.allElementsSelected ? [] : this.selectedElements.map(e => e.id);
                        this.actionListWidgetV2Service.bulkAddToWorkOrder(this.searchFields, this.advancedSearchBody, result.workOrder.id,
                            elementIds, this.allElementsSelected).then(resp => {
                                this.clearSelectedElements();
                                this.initWorkOrders();
                                this.refreshList();
                                this.showSnackbar(`${resp.count} ${this.localizationPipe.transform('actions added to work order')}`);
                            }).catch(err => this.error = ErrorUtility.getMessage(err));
                    }
                }
            });
        } else {
            this.createWorkOrder(true).then(() => {
                this.clearSelectedElements();
                this.refreshList();
            });
        }
    }

    private removeFromWorkOrder(): void {
        const elementIds = this.allElementsSelected ? [] : this.selectedElements.map(e => e.id);
        this.actionListWidgetV2Service.bulkRemoveFromWorkOrder(this.searchFields, this.advancedSearchBody, this.workOrderContext?.id, elementIds, this.allElementsSelected).then(resp => {
            this.clearSelectedElements();
            this.actionListWidgetV2Service.getWorkOrder(this.workOrderContext.id).then(wo => this.setWorkOrderContext(wo));
            this.showSnackbar(`${resp.count} ${this.localizationPipe.transform('actions removed from work order')}`);
        }).catch(err => this.error = ErrorUtility.getMessage(err));
    }

    editWorkOrder(statusOnly: boolean = false): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = false;
        dialogConfig.minWidth = '420px';
        const isHistoricalWorkOrder = this.isHistorical(this.workOrderContext);
        dialogConfig.data = {
            workOrder: this.workOrderContext,
            statusOnly: statusOnly,
            isHistorical: isHistoricalWorkOrder
        }
        this.dialog.open(WorkOrderEditDialogComponent, dialogConfig).afterClosed().pipe(take(1)).subscribe(body => {
            if (body) {
                if (this.isHistorical(body)) {
                    this.loaded = false;
                }
                this.actionListWidgetV2Service.updateWorkOrder(this.workOrderContext.id, body, isHistoricalWorkOrder).then(workOrder => {
                    if (this.isHistorical(workOrder)) {
                        this.setWorkOrderContext(workOrder);
                    } else {
                        this.actionListWidgetV2Service.getWorkOrder(workOrder.id).then(wo => this.setWorkOrderContext(wo));
                    }
                }).catch(err => this.error = ErrorUtility.getMessage(err));
            }
        });
    }

    openWorkOrderHistory(): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '75%';
        dialogConfig.autoFocus = false;
        dialogConfig.data = {
            thingId: this.thingContextService.getCurrentThing().id,
            actionListWidgetV2Service: this.actionListWidgetV2Service,
        }
        this.isWorkOrderHistoryDialogOpen = true;
        this.dialog.open(WorkOrderListDialogComponent, dialogConfig).afterClosed().pipe(take(1)).subscribe(historicalWorkOrder => {
            if (historicalWorkOrder) {
                this.setWorkOrderContext(historicalWorkOrder);
            } else {
                this.isWorkOrderHistoryDialogOpen = false;
            }
        });
    }

    private bulkMarkAsDiscarded(): void {
        this.openDiscardDialog();
    }

    private bulkDeleteWorkOrderNative(): void {
        const elementIds = this.allElementsSelected ? [] : this.selectedElements.map(e => e.id);
        this.actionListWidgetV2Service.bulkDeleteWorkOrderNative(this.searchFields, this.advancedSearchBody, this.workOrderContext?.id, elementIds, this.allElementsSelected).then(resp => {
            this.clearSelectedElements();
            this.actionListWidgetV2Service.getWorkOrder(this.workOrderContext.id).then(wo => this.setWorkOrderContext(wo));
            this.showSnackbar(`${resp.count} ${this.localizationPipe.transform('actions deleted')}`);
        }).catch(err => this.error = ErrorUtility.getMessage(err));
    }

    openSparePartsDialog(): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.width = '428px';
        dialogConfig.autoFocus = false;
        dialogConfig.data = {
            workOrder: this.workOrderContext,
            actionListWidgetV2Service: this.actionListWidgetV2Service,
        }
        this.dialog.open(WorkOrderSparePartListDialogComponent, dialogConfig);
    }

    updateWorkingHours(): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = false;
        dialogConfig.maxWidth = '428px';
        dialogConfig.data = { workingHours: this.workingHours, simulatedWorkingHours: this.simulatedWorkingHours }
        this.dialog.open(ResyncActionsDialogComponent, dialogConfig).afterClosed().pipe(take(1)).subscribe(result => {
            if (result) {
                const thing = this.thingContextService.getCurrentThing();
                let properties = thing.properties || {};
                properties.simulated_working_hours = result.simulatedWorkingHours;
                this.simulatedWorkingHours = result.simulatedWorkingHours;
                firstValueFrom(this.dashboardAreaThingService.updateThing(thing.id, thing)).then(() => {
                    this.resync();
                });
            }
        });
    }

    private resync(): void {
        this.actionSyncLoading = true;
        this.userActionStatusService.resynchronizeActions(this.thingId).then(() => {
            this.clearSelectedElements();
            this.refreshList();
            this.actionSyncLoading = false;
            this.actionSyncTimestampMap[this.thingId] = new Date().getTime();
            this.updateActionSyncTimestampMapLocalStorage();
            if (this.updateWorkingHoursButtonVisible) {
                this.thingService.getThingById(this.thingId, null).then(() => {
                    this.getWorkingHoursPropertyValues();
                });
            }
        }).catch(() => this.actionSyncLoading = false);
    }

    private getWorkingHoursPropertyValues(): void {
        const thing = this.thingContextService.getCurrentThing();
        this.workingHours = _.get(thing, 'properties.working_hours', null);
        this.simulatedWorkingHours = _.get(thing, 'properties.simulated_working_hours', null);
    }

    private getActionSyncTimestampMapFromLocalStorage(): { [thingId: string]: number } {
        return localStorage.getItem(ActionListWidgetV2Component.ACTION_SYNC_TIMESTAMP_MAP_KEY) ? JSON.parse(localStorage.getItem(ActionListWidgetV2Component.ACTION_SYNC_TIMESTAMP_MAP_KEY)) : {};
    }

    private updateActionSyncTimestampMapLocalStorage(): void {
        localStorage.setItem(ActionListWidgetV2Component.ACTION_SYNC_TIMESTAMP_MAP_KEY, JSON.stringify(this.actionSyncTimestampMap));
    }

    downloadReport(): void {
        const body = {
            userId: this.authenticationService.getUser().id,
            url: window.location.origin + '/dashboard/work-order-report/' + this.workOrderContext.id
        }
        const params = new HttpParams().set('timezone', this.timezoneContextService.getTimezone());
        firstValueFrom(this.httpService.post<ExportReportJobKey>(EXPORT_REPORT, body, params)).then(resp => {
            const downloadingObject = {
                fileName: this.resolveWorkOrderReportFileNamePlaceholders(),
                uuid: resp.uuid,
                status: DownloadStatus.DOWNLOADING,
                type: DownloadType.PDF
            }
            this.downloadService.addDownloadingObject(downloadingObject);
            this.downloadService.setVisible();
        }).catch(err => {
            console.error(err);
        });
    }

    private resolveWorkOrderReportFileNamePlaceholders(): string {
        let fileName = this.authenticationService.getTenant().workOrderReportFileName || ActionListWidgetV2Component.DEFAULT_WORK_ORDER_REPORT_FILE_NAME;
        let placeholderValueMap: { [placeholder: string]: string } = {};
        placeholderValueMap['thing.serialNumber'] = this.thingContextService.getCurrentThing().serialNumber || '';
        placeholderValueMap['thing.productModel.name'] = this.thingContextService.getCurrentThing().productModel?.name || '';
        placeholderValueMap['workOrder.code'] = this.workOrderContext.code || '';
        placeholderValueMap['workOrder.startTimestamp.toDateFormat'] = this.datetimeFormatterPipe.transform(this.workOrderContext.startTimestamp, ActionListWidgetV2Component.WORK_ORDER_REPORT_FILE_NAME_DATE_FORMAT);
        Object.keys(placeholderValueMap).forEach(key => {
            const placeholder = '${' + key + '}';
            fileName = fileName.replace(placeholder, placeholderValueMap[key]);
        });
        return fileName;
    }

}

enum ActionListWidgetStatus {
    UPCOMING = 'UPCOMING',
    HISTORICAL = 'HISTORICAL',
    ALL = 'ALL'
}